import {
  BOTTOM_MENU_BUTTONS,
  type BottomMenuButtonKey,
} from './bottom-menu-layout';

export type SecondaryMenuPartVisibility = {
  bg: number;
  icon: number;
};

export type MainMenuPartVisibility = {
  bg: number;
  frame: number;
  geode: number;
};

export type BottomMenuVisibility = {
  bar: number;
  rank: SecondaryMenuPartVisibility;
  booga: SecondaryMenuPartVisibility;
  main: MainMenuPartVisibility;
  weapon: SecondaryMenuPartVisibility;
  shop: SecondaryMenuPartVisibility;
};

const SECONDARY_VISIBLE: SecondaryMenuPartVisibility = { bg: 1, icon: 1 };
const SECONDARY_HIDDEN: SecondaryMenuPartVisibility = { bg: 0, icon: 0 };

export function defaultBottomMenuVisibility(): BottomMenuVisibility {
  return {
    bar: 1,
    rank: { ...SECONDARY_VISIBLE },
    booga: { ...SECONDARY_VISIBLE },
    main: { bg: 1, frame: 1, geode: 1 },
    weapon: { ...SECONDARY_VISIBLE },
    shop: { ...SECONDARY_VISIBLE },
  };
}

/** Progressive reveal presets — layout slots stay fixed; only opacity changes. */
export const BOTTOM_MENU_REVEAL = {
  hidden: (): BottomMenuVisibility => ({
    bar: 0,
    rank: { ...SECONDARY_HIDDEN },
    booga: { ...SECONDARY_HIDDEN },
    main: { bg: 0, frame: 0, geode: 0 },
    weapon: { ...SECONDARY_HIDDEN },
    shop: { ...SECONDARY_HIDDEN },
  }),
  barOnly: (): BottomMenuVisibility => ({
    bar: 1,
    rank: { ...SECONDARY_HIDDEN },
    booga: { ...SECONDARY_HIDDEN },
    main: { bg: 0, frame: 0, geode: 0 },
    weapon: { ...SECONDARY_HIDDEN },
    shop: { ...SECONDARY_HIDDEN },
  }),
  mainOnly: (): BottomMenuVisibility => {
    const v = BOTTOM_MENU_REVEAL.barOnly();
    v.main = { bg: 1, frame: 1, geode: 1 };
    return v;
  },
  full: defaultBottomMenuVisibility,
} as const;

function clampAlpha(value: number) {
  return Math.min(1, Math.max(0, value));
}

function mergeSecondary(
  base: SecondaryMenuPartVisibility,
  patch?: Partial<SecondaryMenuPartVisibility>,
): SecondaryMenuPartVisibility {
  if (!patch) return base;
  return {
    bg: patch.bg !== undefined ? clampAlpha(patch.bg) : base.bg,
    icon: patch.icon !== undefined ? clampAlpha(patch.icon) : base.icon,
  };
}

export type BottomMenuVisibilityPatch = {
  bar?: number;
  rank?: Partial<SecondaryMenuPartVisibility>;
  booga?: Partial<SecondaryMenuPartVisibility>;
  main?: Partial<MainMenuPartVisibility>;
  weapon?: Partial<SecondaryMenuPartVisibility>;
  shop?: Partial<SecondaryMenuPartVisibility>;
};

export function mergeBottomMenuVisibility(
  current: BottomMenuVisibility,
  patch: BottomMenuVisibilityPatch,
): BottomMenuVisibility {
  return {
    bar: patch.bar !== undefined ? clampAlpha(patch.bar) : current.bar,
    rank: mergeSecondary(current.rank, patch.rank),
    booga: mergeSecondary(current.booga, patch.booga),
    main: {
      bg: patch.main?.bg !== undefined ? clampAlpha(patch.main.bg) : current.main.bg,
      frame: patch.main?.frame !== undefined ? clampAlpha(patch.main.frame) : current.main.frame,
      geode: patch.main?.geode !== undefined ? clampAlpha(patch.main.geode) : current.main.geode,
    },
    weapon: mergeSecondary(current.weapon, patch.weapon),
    shop: mergeSecondary(current.shop, patch.shop),
  };
}

export function secondaryMenuVisibility(
  visibility: BottomMenuVisibility,
  key: Exclude<BottomMenuButtonKey, 'main'>,
) {
  return visibility[key];
}

export function isSecondaryInteractive(visibility: SecondaryMenuPartVisibility) {
  return visibility.bg > 0 || visibility.icon > 0;
}

export function isMainInteractive(visibility: MainMenuPartVisibility) {
  return visibility.bg > 0 || visibility.frame > 0 || visibility.geode > 0;
}

export function isBarInteractive(visibility: BottomMenuVisibility) {
  return visibility.bar > 0;
}

/** All button keys for iteration. */
export { BOTTOM_MENU_BUTTONS };
